import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useReducer, useRef, useState } from 'react';
import {
  Keyboard,
  Platform,
  View,
  type KeyboardEvent,
  type LayoutChangeEvent,
  type StyleProp,
  type ViewStyle,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { calculateKeyboardOverlap, calculateKeyboardTop } from './keyboard-responsive-view.utils';

export { calculateKeyboardOverlap, calculateKeyboardTop } from './keyboard-responsive-view.utils';

interface KeyboardResponsiveViewProps extends PropsWithChildren {
  style?: StyleProp<ViewStyle>;
}

interface KeyboardLayoutState {
  overlap: number;
  visible: boolean;
}

const KeyboardVisibleContext = createContext(false);

export function useKeyboardResponsiveViewKeyboardVisible() {
  return useContext(KeyboardVisibleContext);
}

/**
 * Keeps its children inside the portion of this view that is not covered by the
 * software keyboard.
 *
 * Unlike KeyboardAvoidingView, this measures the view in window coordinates.
 * That means native-stack headers, safe areas, bottom tabs, modal offsets, and
 * any other ancestors are already reflected in the calculation.
 */
export function KeyboardResponsiveView({ children, style }: KeyboardResponsiveViewProps) {
  const insets = useSafeAreaInsets();
  const { height: windowHeight } = useWindowDimensions();
  const containerRef = useRef<View>(null);
  const containerBottomRef = useRef<number | undefined>(undefined);
  const keyboardTopRef = useRef<number | undefined>(undefined);
  const keyboardVisibleRef = useRef(false);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const measurementSequenceRef = useRef(0);
  const mountedRef = useRef(true);
  const [keyboardLayout, setKeyboardLayout] = useState<KeyboardLayoutState>({ overlap: 0, visible: false });

  const [scheduleContainerMeasurement] = useReducer(
    (current: () => void) => current,
    undefined,
    () => () => {
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      animationFrameRef.current = requestAnimationFrame(() => {
        animationFrameRef.current = undefined;
        const sequence = ++measurementSequenceRef.current;
        containerRef.current?.measureInWindow((_x, y, _width, height) => {
          if (!mountedRef.current || sequence !== measurementSequenceRef.current) return;
          containerBottomRef.current = y + height;
          const nextOverlap = calculateKeyboardOverlap(
            containerBottomRef.current,
            keyboardTopRef.current,
            keyboardVisibleRef.current,
          );
          setKeyboardLayout((currentLayout) =>
            currentLayout.overlap === nextOverlap && currentLayout.visible === keyboardVisibleRef.current
              ? currentLayout
              : { overlap: nextOverlap, visible: keyboardVisibleRef.current },
          );
        });
      });
    },
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      measurementSequenceRef.current += 1;
      if (animationFrameRef.current !== undefined) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }
    };
  }, []);

  useEffect(() => {
    if (Platform.OS === 'web') return;

    const updateKeyboardLayout = () => {
      const nextOverlap = calculateKeyboardOverlap(
        containerBottomRef.current,
        keyboardTopRef.current,
        keyboardVisibleRef.current,
      );
      setKeyboardLayout((currentLayout) =>
        currentLayout.overlap === nextOverlap && currentLayout.visible === keyboardVisibleRef.current
          ? currentLayout
          : { overlap: nextOverlap, visible: keyboardVisibleRef.current },
      );
    };
    const getKeyboardTop = (event: KeyboardEvent) =>
      calculateKeyboardTop(
        event.endCoordinates.screenY,
        event.endCoordinates.height,
        windowHeight,
        insets.top,
        insets.bottom,
        Platform.OS === 'android',
      );
    const handleKeyboardShow = (event: KeyboardEvent) => {
      keyboardVisibleRef.current = true;
      keyboardTopRef.current = getKeyboardTop(event);
      Keyboard.scheduleLayoutAnimation(event);
      if (Platform.OS === 'ios') updateKeyboardLayout();
      scheduleContainerMeasurement();
    };
    const handleKeyboardHide = (event: KeyboardEvent) => {
      keyboardVisibleRef.current = false;
      keyboardTopRef.current = undefined;
      Keyboard.scheduleLayoutAnimation(event);
      updateKeyboardLayout();
      scheduleContainerMeasurement();
    };
    const handleKeyboardFrameChange = (event: KeyboardEvent) => {
      keyboardVisibleRef.current = event.endCoordinates.height > 0 && event.endCoordinates.screenY < windowHeight;
      keyboardTopRef.current = keyboardVisibleRef.current ? getKeyboardTop(event) : undefined;
      Keyboard.scheduleLayoutAnimation(event);
      if (Platform.OS === 'ios') updateKeyboardLayout();
      scheduleContainerMeasurement();
    };

    const metrics = Keyboard.metrics();
    if (Keyboard.isVisible() && metrics) {
      keyboardVisibleRef.current = true;
      keyboardTopRef.current = calculateKeyboardTop(
        metrics.screenY,
        metrics.height,
        windowHeight,
        insets.top,
        insets.bottom,
        Platform.OS === 'android',
      );
      updateKeyboardLayout();
      scheduleContainerMeasurement();
    }

    const subscriptions =
      Platform.OS === 'ios'
        ? [
            Keyboard.addListener('keyboardWillShow', handleKeyboardShow),
            Keyboard.addListener('keyboardWillHide', handleKeyboardHide),
            Keyboard.addListener('keyboardWillChangeFrame', handleKeyboardFrameChange),
          ]
        : [
            Keyboard.addListener('keyboardDidShow', handleKeyboardShow),
            Keyboard.addListener('keyboardDidHide', handleKeyboardHide),
          ];

    return () => {
      subscriptions.forEach((subscription) => subscription.remove());
    };
  }, [insets.bottom, insets.top, scheduleContainerMeasurement, windowHeight]);

  const handleLayout = (_event: LayoutChangeEvent) => {
    scheduleContainerMeasurement();
  };

  if (Platform.OS === 'web') {
    return <>{children}</>;
  }

  return (
    <View
      collapsable={false}
      onLayout={handleLayout}
      ref={containerRef}
      style={[{ flex: 1, minHeight: 0 }, style, { paddingBottom: keyboardLayout.overlap }]}
    >
      <KeyboardVisibleContext.Provider value={keyboardLayout.visible}>{children}</KeyboardVisibleContext.Provider>
    </View>
  );
}
